import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css';
import { Link, useNavigate, useLocation } from "react-router-dom"
import { getJwt, postJwt, deleteJwt, putJwt } from '../../helpers/clearnet_api_helper';
import { useCSHMessage } from "./useCSHMessage";
import { useCSHDialog } from "./useCSHDialog";
import { getUserInfo } from './CSHHelper';
import './ClearStoneHub.css';
import { identity, initial } from 'lodash';

const DEFAULT_WIDTHS = [40, 320, 180, 220, 100, 60];

const DEFAULT_CRITERIA = {
  search: ''
};


// Add fixed row cell CSS for robust row height and ellipsis
const fixedRowCellStyle = {
  height: 24,
  lineHeight: '24px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
  maxWidth: 320,
  padding: 0,
};

///////////////////////////////////////////////////////////////////////////////////////////// 
// Declare functions used in initialization before they are used - ouside of the component
/////////////////////////////////////////////////////////////////////////////////////////////
// const getUserInfo = () => {
//   const token = localStorage.getItem('csh_jwt_token');
//   if (!token) return { userEmail: '', userRole: '' };
//   try {
//     const payload = JSON.parse(atob(token.split('.')[1]));
//     return { userEmail: payload.sub || '', userRole: payload.role || '' };
//   } catch {
//     return { userEmail: '', userRole: '' };
//   }
// };


const ItemsList = ({ parentId, onFolderChange, onForceRefresh, onLogout }) => {
  const [criteria, setCriteria] = useState({});
  const [colWidths, setColWidths] = useState(DEFAULT_WIDTHS);
  const [selectedItems, setSelectedItems] = useState([]);
  const [items, setItems] = useState([]);
  const [skipCount, setSkipCount] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isLoading, setIsLoading] = useState(false);
  const [hasMoreRecords, setHasMoreRecords] = useState(true);
  const observerRef = useRef(null); // Ref for the intersection observer - used for infinite scrolling
  const [error, setError] = useState(null);
  const [selectAll, setSelectAll] = useState(false);
  const [isWorkingOnItem, setIsWorkingOnItem] = useState(null);
  const [searchValue, setSearchValue] = useState('');

  const [showMessage, message] = useCSHMessage();
  const [showDialog, dialog] = useCSHDialog();
  const [contextMenu, setContextMenu] = useState(null); // { id, x, y }

  // Close context menu on outside click
  useEffect(() => {
    if (!contextMenu) return;
    const handler = () => setContextMenu(null);
    // Defer so the current opening click doesn't immediately close the menu
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handler);
    }, 0);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handler);
    };
  }, [contextMenu]);


  const { userEmail, userRole } = getUserInfo();

  // hack to prevent triggering onBlur for Search when embedded clear (X) button is clicked - we want to allow user to clear the search input and trigger search with empty value by clicking the X button without having to click Enter or move focus away
  const ignoreSearchOnBlurWhenXButtonClickedRef = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();


  // Keep parentId refs updated with latest values to avoid React's stale closures in async functions
  const parentIdRef = useRef(parentId);
  useEffect(() => {
    parentIdRef.current = parentId;
  }, [parentId]);

  // Keep selectAll ref updated to reflect the latest value in async operations
  const selectAllRef = useRef(selectAll);
  useEffect(() => {
    selectAllRef.current = selectAll;
  }, [selectAll]);


  useEffect(() => {
    setItems([]); // Clear items when parentId changes
    setSkipCount(0); // Reset skipCount when parentId changes 
    setHasMoreRecords(true); // Reset hasMoreRecords when parentId changes
    setSelectedItems([]); // Clear selected items when parentId changes
    setSelectAll(false); // Reset selectAll when parentId changes

    async function fetchData() {
      const params = new URLSearchParams(location.search);
      const search = params.get('search') || '';

      setCriteria(search ? { search } : {}); // Update criteria with search term if it exists, otherwise set to empty object);
      setSearchValue(search); // Update the search input value to reflect the current search term from URL

      setIsLoading(true);
      await fetchItems(parentId, { search }, 0, true);
      setIsLoading(false);
    }

    fetchData();
  }, [parentId, location.search]); // Re-run when parentId or search query changes




  const fetchItems = async (parentId, crit = criteria, skip = skipCount, moreRecords = hasMoreRecords) => {
    if (!moreRecords) return; // Stop fetching if no more records are available
    try {
      console.log('fetchItems called with parentId:', parentId, 'skip:', skip, 'search:', crit.search);
      const response = await getJwt(process.env.REACT_APP_CSHUB_API_URL_ITEMS, {
        parentId,
        skipCount: skip,
        pageSize,
        search: crit.search,
        sort: getSortValue()
      });
      console.log('items response:', response);  // check this
      const raw = Array.isArray(response) ? response
        : Array.isArray(response?.Items) ? response.Items
        : [];
      const data = raw.map(item => ({
        id: item.id ?? item.Id,
        name: item.name ?? item.Name,
        isFolder: item.isFolder ?? item.IsFolder,
        parentId: item.parentId ?? item.ParentId,
        lastUpdatedDate: item.lastUpdatedDate ?? item.LastUpdatedDate,
        lastUpdatedByEmail: item.lastUpdatedByEmail ?? item.LastUpdatedByEmail,
        fileSize: item.fileSize ?? item.FileSize,
        description: item.description ?? item.Description,
      }));
      console.log(`Number of records fetched: ${data.length}`); // Log the number of records
      if (data.length < pageSize) {
        setHasMoreRecords(false); // No more records available
      }
      if (data.length > 0) {
        setItems((prevItems) => {
          const newItems = data.filter(
            (newItem) => !prevItems.some((existingItem) => existingItem.id === newItem.id)
          );
          // Only auto-select new items if selectAll is true (using ref for latest value)
          if (selectAllRef.current) {
            setSelectedItems((prevSelected) => [
              ...prevSelected,
              ...newItems.map((item) => item.id).filter((id) => !prevSelected.includes(id))
            ]);
          }
          return [...prevItems, ...newItems];
        });
        setSkipCount((prevSkipCount) => prevSkipCount + pageSize);
        setError(null);
      }
    } catch (error) {
      setHasMoreRecords(false); // No more records available - to prevent infinte reloading/scrolling
      console.error('Error fetching items:', error);
      setError(error?.message || 'Failed to load folders');
    } finally {
    }
  };

  // Infinite scroll observer
  useEffect(() => {
    if (!hasMoreRecords) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading) {
          fetchItems(parentId, criteria, skipCount);
        }
      },
      { rootMargin: '100px' }
    );

    const currentRef = observerRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
      observer.disconnect();
    };
  }, [isLoading, hasMoreRecords, criteria, skipCount]);

  // Helper to format date like Windows Explorer (MM/DD/YYYY h:mm AM/PM)
  function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;
    const pad = (n) => n.toString().padStart(2, '0');
    let hours = date.getHours();
    const minutes = pad(date.getMinutes());
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()} ${hours}:${minutes} ${ampm}`;
  }

  const handleUpFolder = async () => {

    if (!parentId) return; // Do nothing for root level

    try {

      const response = await getJwt(`${process.env.REACT_APP_CSHUB_API_URL_ITEMS}/${parentId}`) // pass parentId as route param

      var upFolderId = response?.parentId; // Get parentId of the current folder

      onFolderChange(upFolderId); // Call onFolderChange with the upFolderId to navigate up

    } catch (error) {

      console.error('Error fetching items:', error);

    }
  };

  const handleHomeFolder = async () => {

    if (!parentId) return; // Do nothing for root level

    try {

      onFolderChange(null); // Call onFolderChange with the null to navigate to home/root

    } catch (error) {

      console.error('Error fetching items:', error);

    }
  };

  const onSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setSkipCount(0);
    setItems([]);
    setHasMoreRecords(true);
    // Do NOT call fetchItems here - rely on useEffect to trigger fetch with new sort criteria
  };

  const getSortValue = () => {
    return sortDirection === 'desc' ? `${sortField}_desc` : sortField;
  };

  const isChecked = (id) => selectedItems.includes(id);

  const handleSelectAll = (e) => {
    if (e.stopPropagation) e.stopPropagation();
    const shouldSelect = e.target.checked;
    setSelectAll(shouldSelect);
    if (shouldSelect) {
      setSelectedItems(items.map(item => item.id));
    } else {
      setSelectedItems([]);
    }
  };

  const toggleItemSelection = (id) => {
    setSelectedItems((prev) => {
      if (prev.includes(id)) {
        // If selectAll is true and user unchecks a row, turn off selectAll
        if (selectAll) setSelectAll(false);
        return prev.filter(itemId => itemId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const isAllSelected = selectAll && items.length > 0 && selectedItems.length === items.length;

  // Handle column resize
  const handleResize = (index, e, { size }) => {
    setColWidths((widths) => {
      const next = [...widths];
      next[index] = size.width;
      return next;
    });
  };


  const MIME_TYPES = {
    'pdf': 'application/pdf',
    'txt': 'text/plain',
    'csv': 'text/csv',
    'html': 'text/html',
    'htm': 'text/html',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'bmp': 'image/bmp',
    'webp': 'image/webp',
    'svg': 'image/svg+xml',
  };

  // File types browsers cannot display inline — trigger download instead
  const DOWNLOAD_TYPES = new Set(['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'zip', 'rar', '7z', 'msg']);

  const onOpenFile = async (id) => {
    setIsWorkingOnItem(id);

    try {

      const rsp = await getJwt(process.env.REACT_APP_CSHUB_API_URL_FILE_REQUEST, { Id: id }); // pass item id to backend to get the storageLocation of the file

      console.log('File request response:', rsp); // Log the entire response for debugging

      var documentPath = rsp?.url ?? rsp?.Item?.URL; // Get pre-signed URL of the file
      var fileName = rsp?.name ?? rsp?.Item?.Name; // Get original file name from response if available

      console.log(`Opening file with id: ${id}, documentPath: ${documentPath}, fileName: ${fileName}`); // Log the document path and file name

      //const documentPath = convertUncToHttp(storageLocation);
      const urlPathOnly = documentPath.split('?')[0]; // strip query string before extracting ext/name
      const ext = (fileName || urlPathOnly.split(/[\\/]/).pop()).split('.').pop().toLowerCase();
      const mimeType = MIME_TYPES[ext] || 'application/octet-stream';

      console.log(`File extension: ${ext}, MIME type: ${mimeType}`); // Log the file extension and MIME type

      const response = await fetch(documentPath);
      const base64String = (await response.text()).trim();
      const byteCharacters = atob(base64String);
      const byteArray = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteArray[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([byteArray], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);

      if (DOWNLOAD_TYPES.has(ext)) {
        const filename = fileName || urlPathOnly.split(/[\\/]/).pop() || `download.${ext}`;
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } else {
        window.open(blobUrl, "_blank");
      }
    }
    catch (error) {
      await showMessage({ message: "Error opening file: " + (error?.message || 'Unknown error'), messageType: "danger" });
    }

    setIsWorkingOnItem(null);
  };


  const onDownloadFile = async (id) => {
    try {
      setIsWorkingOnItem(true);

      await downloadFile(id);

      setIsWorkingOnItem(null);

      await showMessage({ message: "Download complete. Please check your downloads folder.", messageType: "success" });

    } catch (error) {
      setIsWorkingOnItem(null);
      await showMessage({ message: "Error downloading file: " + (error?.message || 'Unknown error'), messageType: "danger" });
    }
  }


  const downloadFile = async (id) => {
    setIsWorkingOnItem(id);
    try {

      const rsp = await getJwt(process.env.REACT_APP_CSHUB_API_URL_FILE_REQUEST, { Id: id }); // pass item id to backend to get the storageLocation of the file

      var documentPath = rsp?.url ?? rsp?.Item?.URL; // Get pre-signed URL of the file
      var fileName = rsp?.name ?? rsp?.Item?.Name; // Get original file name from response if available

      console.log(`Downloading file with id: ${id}, documentPath: ${documentPath}, fileName: ${fileName}`); // Log the document path and file name


      //const documentPath = convertUncToHttp(storageLocation);
      const urlPathOnly = documentPath.split('?')[0]; // strip query string before extracting ext/name
      const ext = (fileName || urlPathOnly.split(/[\\/]/).pop()).split('.').pop().toLowerCase();
      const mimeType = MIME_TYPES[ext] || 'application/octet-stream';

      console.log(`File extension: ${ext}, MIME type: ${mimeType}`); // Log the file extension and MIME type

      const response = await fetch(documentPath);
      const base64String = (await response.text()).trim();
      const byteCharacters = atob(base64String);
      const byteArray = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteArray[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([byteArray], { type: mimeType });
      const filename = fileName || urlPathOnly.split(/[\\/]/).pop() || `download.${ext}`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.log("Error downloading document: " + error.message);
    }
    setIsWorkingOnItem(null);
  };

  function convertUncToHttp(uncPath) {
    const server = process.env.REACT_APP_CLEARSTONEHUB_DOWNLOAD_SERVER;
    // Replace backslashes with slashes and strip leading slashes
    let url = uncPath.replace(/\\/g, '/').replace(/^\/+/, '');
    // Strip the UNC server name (first path segment) and use the env var instead
    const slashIdx = url.indexOf('/');
    const path = slashIdx !== -1 ? url.substring(slashIdx + 1) : url;
    // Encode each path segment individually
    const encodedPath = path.split('/').map(encodeURIComponent).join('/');
    return `http://${server}/${encodedPath}`;
  }


  const handleRenameItem = async (id, itemParentId, name, isFolder) => {

    const newName = await showDialog({
      title: `Rename ${isFolder ? "Folder" : "File"}`,
      placeholder: `${isFolder ? "Folder" : "File"} name`,
      confirmText: "Rename",
      mode: "inputvalue",
      initialValue: name
    });

    if (newName) {

      try {
        // Check for duplicate before creating
        const isDuplicate = await checkDuplicate(newName, itemParentId);

        if (isDuplicate) {

          await showMessage({ message: "Unable to rename.An item with this name already exists in this location.", messageType: "warning" });
          //setError("An item with this name already exists in this location.");
          return;
        }

        setIsWorkingOnItem(true);
        renameItem(id, newName);
        setIsWorkingOnItem(false);


      } catch (error) {
        await showMessage({ message: "Error renaming item: " + (error?.message || 'Unknown error'), messageType: "danger" });
      }
    }
  };


  const handleUpdateFile = async (id, itemParentId, name, description) => {

    const result = await showDialog({
      title: "Update File",
      placeholder: "File name",
      confirmText: "Update",
      mode: "upload",
      initialValue: name,
      initialDescription: description
    });

    if (result && result.file) {

      // result.file is a File object, result.name is the chosen name
      // console.log(result.file.name); // original filename
      // console.log(result.file.size); // file size in bytes

      try {

        var fileName = result.file.name.trim();
        var fileDescription = result.description.trim();

        // Check for duplicate before creating - Only check if the name was changed - allow updating file without changing the name
        if (fileName !== name) {

          const isDuplicate = await checkDuplicate(fileName, itemParentId);

          if (isDuplicate) {

            await showMessage({ message: "An item with this name already exists in this location.", messageType: "warning" });

            return;
          }
        }

        setIsWorkingOnItem(true);
        await updateFile(id, fileName, fileDescription, result.file);
        setIsWorkingOnItem(null);


      } catch (error) {
        setIsWorkingOnItem(null);
        await showMessage({ message: "Error updating file: " + (error?.message || 'Unknown error'), messageType: "danger" });
      }
    }
  };



  const handleAddFolder = async () => {

    const name = await showDialog({
      title: "Create Folder",
      placeholder: "Folder name",
      confirmText: "Create",
      mode: "inputvalue"
    });

    if (name) {

      try {
        // Check for duplicate before creating
        const isDuplicate = await checkDuplicate(name, parentId);

        if (isDuplicate) {

          await showMessage({ message: "An item with this name already exists in this location.", messageType: "warning" });
          //setError("An item with this name already exists in this location.");
          return;
        }

        setIsWorkingOnItem(true);
        createFolder(name);
        setIsWorkingOnItem(false);


      } catch (error) {
        await showMessage({ message: "Error creating folder: " + (error?.message || 'Unknown error'), messageType: "danger" });
      }
    }
  };


  const handleAddFile = async () => {

    const result = await showDialog({
      title: "Upload File",
      placeholder: "File name",
      confirmText: "Upload",
      mode: "upload"
    });

    if (result && result.file) {

      // result.file is a File object, result.name is the chosen name
      // console.log(result.file.name); // original filename
      // console.log(result.file.size); // file size in bytes

      try {

        var fileName = result.file.name.trim();
        var fileDescription = result.description.trim();

        // Check for duplicate before creating
        const isDuplicate = await checkDuplicate(fileName, parentId);

        if (isDuplicate) {

          await showMessage({ message: "An item with this name already exists in this location.", messageType: "warning" });

          return;
        }

        setIsWorkingOnItem(true);
        await createFile(fileName, fileDescription, parentId, result.file);
        setIsWorkingOnItem(null);


      } catch (error) {
        setIsWorkingOnItem(null);
        await showMessage({ message: "Error creating file: " + (error?.message || 'Unknown error'), messageType: "danger" });
      }
    }
  };


  async function renameItem(id, name) {


    const data = {
      id,
      name,
      userEmail: userEmail
    };

    const response = await putJwt('api/ClearStoneHub/Items/Rename', data);

    // refresh the items list after creation     
    await refreshComponent(parentId);

    // refhesh the folder tree to show the new folder - we can optimize this later to just add the new folder to the existing tree without reloading everything
    if (onForceRefresh) onForceRefresh();

    setError(null);



  }


  async function createFolder(name) {

    const data = {
      name,
      parentId,
      userEmail: userEmail
    };

    const response = await postJwt('api/ClearStoneHub/Items/CreateFolder', data);

    // refresh the items list after creation     
    await refreshComponent(parentId);

    // refhesh the folder tree to show the new folder - we can optimize this later to just add the new folder to the existing tree without reloading everything
    if (onForceRefresh) onForceRefresh();

    setError(null);

    return response;

  }

  async function refreshComponent(currentdParentId) {

    // In rare cases, user can navigate away while operation was in progress — then skip refresh (avoid adding the new item to different displayed folder)
    if (currentdParentId !== undefined && currentdParentId !== parentIdRef.current) return;

    setItems([]);
    setSkipCount(0);
    setHasMoreRecords(true);
    await fetchItems(parentId, criteria, 0, true);

  }

  async function createFile(name, description, parentId, file) {

    // Serialize the PDF to base64 and send it along with the metadata.
    // The backend writes it to disk, determines storageLocation, and saves the record.
    const fileContent = await fileToBase64(file);

    var fileSize = Math.round(file.size / 1024);

    const data = {
      name,
      description,
      parentId,
      userEmail: userEmail,
      fileContent,
      fileSize
    };

    const response = await postJwt('api/ClearStoneHub/Items/CreateFile', data);

    const uploadUrl = response?.url; // Get the pre-signed upload URL from the response

    if (uploadUrl) {
      await fetch(uploadUrl, {
        method: 'PUT',
        body: fileContent,
        headers: { 'Content-Type': 'application/octet-stream' },
      }); // Upload the file content to the pre-signed URL
    }


    // refresh the items list after creation     
    await refreshComponent(parentId);


    // refresh the folder tree not needed since we added a file not folder
    //if (onForceRefresh) onForceRefresh();

    setError(null);

    return response;
  }


  async function updateFile(id, name, description, file) {

    // Serialize the PDF to base64 and send it along with the metadata.
    // The backend writes it to disk, determines storageLocation, and saves the record.
    const fileContent = await fileToBase64(file);

    var fileSize = Math.round(file.size / 1024);

    const data = {
      id,
      name,
      description,
      parentId,
      userEmail,
      fileContent,
      fileSize
    };

    const response = await putJwt('api/ClearStoneHub/Items/UpdateFile', data);

    const uploadUrl = response?.url; // Get the pre-signed upload URL from the response

    if (uploadUrl) {
      await fetch(uploadUrl, {
        method: 'PUT',
        body: fileContent,
        headers: { 'Content-Type': 'application/octet-stream' },
      }); // Upload the file content to the pre-signed URL
    }

    // refresh the items list after creation     
    await refreshComponent(parentId);


    // refresh the folder tree not needed since we added a file not folder
    //if (onForceRefresh) onForceRefresh();

    setError(null);

    return response;
  }


  const handleRemoveItem = async (id, name, isFolder) => {

    const confirm = await showDialog({
      title: (
        <>
          Are you sure you want to remove <strong>{name}</strong> {isFolder ? 'folder and all its contents' : ''}?
        </>
      ),
      confirmText: "Yes",
      cancelText: "No",
      mode: "confirmation"
    });

    if (confirm) {

      try {

        setIsWorkingOnItem(true);
        await removeItem(id, isFolder);
        setIsWorkingOnItem(null);

      } catch (error) {
        setIsWorkingOnItem(null);
        await showMessage({ message: "Error removing item: " + (error?.message || 'Unknown error'), messageType: "danger" });
      }
    }

  }


  const handleRemoveItems = async () => {

    if (selectedItems.length === 0) {
      await showMessage({ message: "No selection found. Please, select Files or Folders to delete.", messageType: "warning" });
      return;
    }

    const confirm = await showDialog({
      title: (
        <>
          Are you sure you want to remove the selected items?
        </>
      ),
      confirmText: "Yes",
      cancelText: "No",
      mode: "confirmation"
    });

    if (confirm) {

      try {

        setIsWorkingOnItem(true);
        await removeItems(selectedItems);
        setIsWorkingOnItem(null);

      } catch (error) {
        setIsWorkingOnItem(null);
        await showMessage({ message: "Error removing items: " + (error?.message || 'Unknown error'), messageType: "danger" });
      }
    }

  }


  async function removeItem(id, isFolder) {

    const response = await deleteJwt(`api/ClearStoneHub/Items/${id}`, { userEmail: userEmail });

    // refresh the items list after creation     
    await refreshComponent(parentId);

    // refresh the folder tree if a folder was removed
    if (isFolder && onForceRefresh) onForceRefresh();

    setError(null);

    return response;

  }

  async function removeItems(ids) {

    //const response = await deleteJwt(`api/ClearStoneHub/Items/${id}`);
    console.log("Deleting items with ids: " + ids.join(","));

    const response = await deleteJwt('api/ClearStoneHub/Items', { ids, userEmail: userEmail });

    // refresh the items list after creation     
    await refreshComponent(parentId);

    // refresh the folder tree 
    if (onForceRefresh) onForceRefresh();

    setError(null);

    return response;

  }

  const handleDownloadFiles = async () => {

    const selectedFolders = items.filter(
      item => selectedItems.includes(item.id) && item.isFolder
    );

    if (selectedFolders.length > 0) {
      await showMessage({ message: "Folders download is not supported. Please, unselect any selected folders.", messageType: "warning" });
      return;
    }

    const selectedFiles = items.filter(
      item => selectedItems.includes(item.id) && !item.isFolder
    );

    if (selectedFiles.length === 0) {
      await showMessage({ message: "No files selected. Please, select Files to download.", messageType: "warning" });
      return;
    }

    try {
      setIsWorkingOnItem(true);
      for (const file of selectedFiles) {
        await downloadFile(file.id);
      }
      setIsWorkingOnItem(null);

      await showMessage({ message: "Download complete. Please check your downloads folder.", messageType: "success" });

    } catch (error) {
      setIsWorkingOnItem(null);
      await showMessage({ message: "Error downloading files: " + (error?.message || 'Unknown error'), messageType: "danger" });
    }
  };

  // Serializes a PDF File to a base64 string (mirrors atob() used in onDownloadFile)
  const fileToBase64 = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary); // btoa() is the inverse of atob() used in download
  };


  const checkDuplicate = async (name, parentId) => {

    const result = await getJwt(
      `api/ClearStoneHub/Items/checkDuplicate`,
      { name, parentId }
    );

    return result; // Should be true if duplicate, false if not

  };


  const handleSearchChange = (value) => {

    if (value.length > 0 && value.length < 3) {
      showMessage({ message: "Please enter at least 3 characters to search.", messageType: "warning" });
      setSearchValue(criteria.search || ''); // Reset to last applied search if input is cleared or less than 3 chars
      return;
    }

    setCriteria((prev) => ({ ...prev, search: value })); // Update the search field in criteria
    //setIsCriteriaChanged(true); // Mark criteria as changed

    // save search term to URL query params - this will also trigger useEffect to reload the items with new search term
    //const params = new URLSearchParams();
    //if (crit.search !== DEFAULT_CRITERIA.search) 
    //params.set('search', value);

    const params = new URLSearchParams(window.location.search);
    if (value) {
      params.set('search', value);
    } else {
      params.delete('search');
    }
    navigate({ search: params.toString() }, { replace: true });

    // if (value.length >= 3) {
    //   fetchSuggestions(value); // Fetch suggestions if input length is 3 or more
    // } else {
    //   setSuggestions([]); // Clear suggestions if input length is less than 3
    // }
  };

  // const handleLogout = () => {
  //   if (onLogout) onLogout();
  // };

  console.log("process.env.REACT_APP_WEBSITE: ", process.env.REACT_APP_WEBSITE);

  return (
    <React.Fragment>

      <div className="mt-4" style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, cursor: isWorkingOnItem ? 'progress' : undefined }}>

        {error ? (
          <div className="text-center mt-4 text-danger">{error}</div>
        ) : items === null ? (
          <div className="text-center mt-4 text-muted">Loading...</div>
        ) : (
          <>
            <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', width: '100%' }}>
              {/* <h5 className="font-size-16 me-3">Recent Files</h5> */}

              <button id="popoverButton" type="button" aria-haspopup="true" aria-expanded="false" className="btn btn-light btn-sm btn btn-#eff2f7 me-2" style={{ fontSize: "1.25rem" }}
                onClick={handleHomeFolder}
              >
                <i className="fa fa-home" />
              </button>
              <button id="popoverButton" type="button" aria-haspopup="true" aria-expanded="false" className="btn btn-light btn-sm btn btn-#eff2f7 me-2" style={{ fontSize: "1.25rem" }}
                onClick={handleUpFolder}
              >
                <i className="fa fa-arrow-up" />
              </button>

              {userRole === "Admin" && process.env.REACT_APP_WEBSITE === 'CLEARNET' && (
                <>

                  <button id="popoverButton" type="button" aria-haspopup="true" aria-expanded="false" className="btn btn-light btn-sm btn btn-#eff2f7 me-2"
                    style={{ fontSize: "1.25rem", opacity: searchValue.length > 0 ? 0.4 : 1, cursor: searchValue.length > 0 ? 'not-allowed' : 'pointer' }}
                    onClick={handleAddFolder}
                    disabled={searchValue.length > 0}
                  >
                    <i className="fa fa-folder-plus" />
                  </button>

                  <button id="popoverButton" type="button" aria-haspopup="true" aria-expanded="false" className="btn btn-light btn-sm btn btn-#eff2f7 me-2"
                    style={{ fontSize: "1.25rem", opacity: searchValue.length > 0 ? 0.4 : 1, cursor: searchValue.length > 0 ? 'not-allowed' : 'pointer' }}
                    onClick={handleAddFile}
                    disabled={searchValue.length > 0}
                  >
                    <i className="fa fa-plus" />
                  </button>

                  <button
                    type="button"
                    className="btn btn-light btn-sm btn btn-#eff2f7 me-2"
                    style={{ fontSize: "1.25rem", opacity: selectedItems.length === 0 ? 0.4 : 1, cursor: selectedItems.length === 0 ? 'not-allowed' : 'pointer' }}
                    onClick={handleRemoveItems}
                    disabled={selectedItems.length === 0}
                  >
                    <i className="fa fa-trash" />
                  </button>

                </>
              )}

              <button
                type="button"
                className="btn btn-light btn-sm btn btn-#eff2f7 me-2"
                style={{ fontSize: "1.25rem", opacity: selectedItems.length === 0 ? 0.4 : 1, cursor: selectedItems.length === 0 ? 'not-allowed' : 'pointer' }}
                onClick={handleDownloadFiles}
                disabled={selectedItems.length === 0}
              >
                <i className="fa fa-download" />
              </button>



              <div
                style={{
                  position: 'relative',
                  minWidth: process.env.REACT_APP_WEBSITE === 'CLEARNET' ? 200 : 100,
                  maxWidth: 220,
                  flexGrow: 1,
                  marginLeft: 12,
                  marginRight: 12,
                  display: 'flex',
                  justifyContent: 'center',
                  flexShrink: 1,
                }}
              >
                <div style={{
                  position: 'relative',
                  width: '100%',
                  marginLeft: 12,
                  marginRight: 12,
                }}>
                  <span
                    style={{
                      position: 'absolute',
                      left: 14,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#b0b3b9',
                      fontSize: 15,
                      pointerEvents: 'none',
                      zIndex: 2,
                    }}
                  >
                    <i className="fa fa-search" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search..."
                    value={searchValue}
                    onChange={e => setSearchValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        handleSearchChange(searchValue);
                      }
                    }}
                    // hack to prevent triggering onBlur when clear (X) button is clicked - we want to allow user to clear the search input and trigger search with empty value by clicking the X button without having to click Enter or move focus away
                    onBlur={() => {
                      if (ignoreSearchOnBlurWhenXButtonClickedRef.current) {
                        ignoreSearchOnBlurWhenXButtonClickedRef.current = false;
                        return;
                      }
                      handleSearchChange(searchValue);
                    }}

                    style={{
                      paddingLeft: 38,
                      paddingRight: searchValue ? 36 : 12,
                      borderRadius: 24,
                      background: '#f4f6fa',
                      border: '1px solid #e3e6ed',
                      boxShadow: 'none',
                      height: 38,
                      fontSize: 15,
                      color: '#222',
                      transition: 'border 0.2s',
                    }}
                  />

                  <button
                    type="button"
                    onClick={() => { setSearchValue(''); handleSearchChange(''); }}
                    style={{
                      position: 'absolute',
                      right: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: '#b0b3b9',
                      fontSize: 16,
                      cursor: 'pointer',
                      padding: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 3,
                      transition: 'color 0.2s',
                    }}
                    onMouseDown={() => { ignoreSearchOnBlurWhenXButtonClickedRef.current = true; }}
                    onMouseEnter={(e) => e.target.style.color = '#666'}
                    onMouseLeave={(e) => e.target.style.color = '#b0b3b9'}
                    title="Clear search"
                  >
                    <i className="fa fa-times" />
                  </button>

                </div>
              </div>


              <div
                style={{
                  marginLeft: 'auto',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flexShrink: 1,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <div className="user-info" style={{ display: 'flex', alignItems: 'center' }}>
                  <span
                    style={{
                      minWidth: 150,
                      fontWeight: 'bold',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      //marginRight: '12px',
                      display: 'block',
                    }}
                  >
                    {userEmail}
                  </span>
                  {userRole && userRole !== "User" && process.env.REACT_APP_WEBSITE === 'CLEARNET' && (
                    <span
                      style={{
                        minWidth: 50,
                        marginLeft: 8,
                        fontWeight: 'normal',
                        color: '#556ee6',
                        cursor: 'pointer',
                      }}
                      onClick={() => navigate("/ClearStoneHub/Users")}
                      title="Go to Users"
                    >
                      <strong>[{userRole}]</strong>
                    </span>
                  )}
                </div>
                <button
                  id="popoverButton"
                  type="button"
                  aria-haspopup="true"
                  aria-expanded="false"
                  className="btn btn-light btn-sm btn btn-#eff2f7 me-2"
                  style={{ fontSize: "1.25rem", flexShrink: 0, marginLeft: 8 }}
                  onClick={onLogout}
                >
                  <i className="fa fa-sign-out-alt" />
                </button>
              </div>
            </div>
            <hr className="mt-2" />

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'auto' }}>
              <table className="csh-table" style={{ tableLayout: 'fixed', minWidth: colWidths.reduce((a, b) => a + b, 0) }}>
                <colgroup>
                  {colWidths.map((w, i) => (
                    <col key={i} style={{ width: w }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th
                      className="csh-sticky-header"
                      style={{
                        position: 'sticky',
                        top: 0,
                        //background: '#fff',
                        zIndex: 10, // ensure header stays above body checkboxes
                        textAlign: 'center',
                        width: colWidths[0],
                        cursor: 'pointer'
                      }}
                      onClick={(e) => {
                        // trigger the "Select All" logic when the cell is clicked
                        handleSelectAll({
                          target: { checked: !isAllSelected },
                          stopPropagation: () => e.stopPropagation()
                        });
                      }}
                    >
                      <input
                        type="checkbox"
                        //className="csh-table-checkbox"
                        checked={isAllSelected}
                        readOnly
                        style={{ pointerEvents: 'none' }}
                        aria-label="Select all"
                      />
                    </th>
                    <th className="csh-sticky-header" style={{
                      position: 'sticky',
                      top: 0,
                      //background: '#fff',
                      zIndex: 2,
                      width: colWidths[1]
                    }}>
                      <ResizableBox
                        width={colWidths[1]}
                        height={30}
                        axis="x"
                        minConstraints={[80, 30]}
                        maxConstraints={[500, 30]}
                        handle={
                          <span style={{
                            position: 'absolute', right: -3, top: 4, bottom: 4, width: 6, cursor: 'col-resize', zIndex: 3,
                            background: 'transparent', pointerEvents: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            <span style={{ width: 2, height: '100%', background: '#b6b6b6', borderRadius: 1, opacity: 0.7 }} />
                          </span>
                        }
                        onResize={(e, data) => handleResize(1, e, data)}
                        resizeHandles={["e"]}
                        style={{ width: '100%', boxSizing: 'border-box', height: '100%' }}
                      >
                        <div style={{ width: '100%', height: '100%', overflow: 'hidden', padding: '0 4px 0 8px', display: 'flex', alignItems: 'center', boxSizing: 'border-box' }}>
                          <button type="button" className="btn btn-link" onClick={() => onSort('name')} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: 0 }}>
                            <strong>Name</strong>
                          </button>
                          &nbsp;&nbsp;{sortField === 'name' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                        </div>
                      </ResizableBox>
                    </th>
                    <th className="csh-sticky-header" style={{
                      position: 'sticky',
                      top: 0,
                      //background: '#fff',
                      zIndex: 2,
                      width: colWidths[2]
                    }}>
                      <ResizableBox
                        width={colWidths[2]}
                        height={30}
                        axis="x"
                        minConstraints={[80, 30]}
                        maxConstraints={[500, 30]}
                        handle={
                          <span style={{
                            position: 'absolute', right: -3, top: 4, bottom: 4, width: 6, cursor: 'col-resize', zIndex: 3,
                            background: 'transparent', pointerEvents: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            <span style={{ width: 2, height: '100%', background: '#b6b6b6', borderRadius: 1, opacity: 0.7 }} />
                          </span>
                        }
                        onResize={(e, data) => handleResize(2, e, data)}
                        resizeHandles={["e"]}
                        style={{ width: '100%', boxSizing: 'border-box', height: '100%' }}
                      >
                        <div style={{ width: '100%', height: '100%', overflow: 'hidden', padding: '0 4px 0 8px', display: 'flex', alignItems: 'center', boxSizing: 'border-box' }}>
                          <button type="button" className="btn btn-link" onClick={() => onSort('lastupdated')} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: 0 }}>
                            <strong>Last modified</strong>
                          </button>
                          &nbsp;&nbsp;{sortField === 'lastupdated' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                        </div>
                      </ResizableBox>
                    </th>
                    <th className="csh-sticky-header" style={{
                      position: 'sticky',
                      top: 0,
                      //background: '#fff',
                      zIndex: 2,
                      width: colWidths[3]
                    }}>
                      <ResizableBox
                        width={colWidths[3]}
                        height={30}
                        axis="x"
                        minConstraints={[60, 30]}
                        maxConstraints={[300, 30]}
                        handle={
                          <span style={{
                            position: 'absolute', right: -3, top: 4, bottom: 4, width: 6, cursor: 'col-resize', zIndex: 3,
                            background: 'transparent', pointerEvents: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            <span style={{ width: 2, height: '100%', background: '#b6b6b6', borderRadius: 1, opacity: 0.7 }} />
                          </span>
                        }
                        onResize={(e, data) => handleResize(3, e, data)}
                        resizeHandles={["e"]}
                        style={{ width: '100%', boxSizing: 'border-box', height: '100%' }}
                      >
                        <div style={{ width: '100%', height: '100%', overflow: 'hidden', padding: '0 4px 0 8px', display: 'flex', alignItems: 'center', boxSizing: 'border-box' }}>
                          <button type="button" className="btn btn-link" onClick={() => onSort('lastupdatedby')} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: 0 }}>
                            <strong>Last modified by</strong>
                          </button>
                          &nbsp;&nbsp;{sortField === 'lastupdatedby' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                        </div>
                      </ResizableBox>
                    </th>
                    <th className="csh-sticky-header" style={{
                      position: 'sticky',
                      top: 0,
                      //background: '#fff',
                      zIndex: 2,
                      width: colWidths[4]
                    }}>
                      <ResizableBox
                        width={colWidths[4]}
                        height={30}
                        axis="x"
                        minConstraints={[60, 30]}
                        maxConstraints={[300, 30]}
                        handle={
                          <span style={{
                            position: 'absolute', right: -3, top: 4, bottom: 4, width: 6, cursor: 'col-resize', zIndex: 3,
                            background: 'transparent', pointerEvents: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            <span style={{ width: 2, height: '100%', background: '#b6b6b6', borderRadius: 1, opacity: 0.7 }} />
                          </span>
                        }
                        onResize={(e, data) => handleResize(4, e, data)}
                        resizeHandles={["e"]}
                        style={{ width: '100%', boxSizing: 'border-box', height: '100%' }}
                      >
                        <div style={{ width: '100%', height: '100%', overflow: 'hidden', padding: '0 4px 0 8px', display: 'flex', alignItems: 'center', boxSizing: 'border-box' }}>
                          <button type="button" className="btn btn-link" onClick={() => onSort('size')} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: 0 }}>
                            <strong>Size</strong>
                          </button>
                          &nbsp;&nbsp;{sortField === 'size' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                        </div>
                      </ResizableBox>
                    </th>
                    <th className="csh-sticky-header" style={{
                      position: 'sticky',
                      top: 0,
                      //background: '#fff',
                      zIndex: 2,
                      width: colWidths[5]
                    }}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item.id || item.name}
                      className={isChecked(item.id) ? 'csh-row-selected' : ''}
                      style={{
                        height: 44,
                        lineHeight: '44px',
                        verticalAlign: 'middle',
                        padding: 0,
                      }}
                      onClick={e => {
                        if (
                          (e.ctrlKey || e.shiftKey) &&
                          e.target.type !== 'checkbox' &&
                          !e.target.closest('.dropdown-menu') &&
                          !e.target.closest('.dropdown-toggle') &&
                          e.target.tagName !== 'BUTTON' &&
                          e.target.tagName !== 'A'
                        ) {
                          toggleItemSelection(item.id);
                        }
                      }}
                      onDoubleClick={e => {
                        if (item.isFolder) {
                          onFolderChange(item.id);
                        } else {
                          onOpenFile(item.id);
                        }
                      }}
                      onMouseDown={e => { if (e.detail === 2) e.preventDefault(); }}
                    >
                      <td
                        style={{
                          textAlign: 'center',
                          cursor: 'pointer',
                          zIndex: 2,
                          position: 'relative',
                          borderTop: '1px solid #e9ecef', // visually separate header and help with overlap
                          //background: '#fff' // ensure background matches header
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleItemSelection(item.id);
                        }}
                      >
                        <input
                          type="checkbox"
                          //className="csh-table-checkbox"
                          checked={isChecked(item.id)}
                          onChange={() => toggleItemSelection(item.id)}
                          // Hack to make it work in YMS where clicks on checkbox also trigger row click due to event delegation on a higher level - we stop propagation on checkbox click to prevent that
                          onClick={process.env.REACT_APP_WEBSITE === 'YMS' ? (e) => e.stopPropagation() : undefined}
                          aria-label={`Select ${item.name}`}
                        />
                      </td>
                      <td style={fixedRowCellStyle}>
                        <span style={{
                          display: 'block',
                          width: colWidths[1],
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          boxSizing: 'border-box'
                        }}>
                          {item.isFolder ? '📁' : null} {item.name}
                        </span>
                      </td>
                      <td style={fixedRowCellStyle}>
                        <span style={{
                          display: 'block',
                          width: colWidths[2],
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          boxSizing: 'border-box'
                        }}>
                          {formatDate(item.lastUpdatedDate)}
                        </span>
                      </td>
                      <td style={fixedRowCellStyle}>
                        <span
                          style={{
                            display: 'block',
                            width: colWidths[3],
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            boxSizing: 'border-box'
                          }}
                        >
                          {item.lastUpdatedByEmail}
                        </span>
                      </td>
                      <td style={fixedRowCellStyle}>
                        <span
                          style={{
                            display: 'block',
                            width: colWidths[4],
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            boxSizing: 'border-box'
                          }}
                        >
                          {!item.isFolder ? `${item.fileSize} KB` : null}
                        </span>
                      </td>
                      <td style={{ ...fixedRowCellStyle, position: 'relative', overflow: 'visible' }}>
                        {(!item.isFolder || (userRole === "Admin" && process.env.REACT_APP_WEBSITE === 'CLEARNET')) && (
                          <button
                            className="font-size-16 text-muted"
                            style={{ background: 'none', border: 'none', padding: '0 4px', cursor: 'pointer' }}
                            onClick={e => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              setContextMenu(prev =>
                                prev && prev.id === item.id ? null : { id: item.id, x: rect.right, y: rect.bottom, item }
                              );
                            }}
                          >
                            <i className="mdi mdi-dots-horizontal"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Place the observer div at the very end of the scrollable area, outside the table */}
              <div ref={observerRef} style={{ height: '1px' }}></div>
            </div>
          </>
        )}

        {/* Loader */}
        <div>
          {isLoading && <div style={{ textAlign: 'center', marginTop: '20px' }}>Loading...</div>}
          {/* {!isLoading && items.length === 0 && <div style={{ textAlign: 'center', marginTop: '20px' }}>No results available</div>} */}
        </div>
      </div>

      {message}
      {dialog}
      {contextMenu && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 9999,
            display: 'block',
            minWidth: '10rem',
            padding: '0.5rem 0',
            margin: 0,
            background: '#fff',
            border: '1px solid rgba(0,0,0,.15)',
            borderRadius: '0.25rem',
            boxShadow: '0 0.5rem 1rem rgba(0,0,0,.175)',
            transform: 'translateX(-100%)',
            fontSize: process.env.REACT_APP_WEBSITE === 'YMS' ? '1.5rem' : '1rem',
            color: '#212529',
            textAlign: 'left',
            listStyle: 'none',
            backgroundClip: 'padding-box',
          }}
          onClick={e => e.stopPropagation()}
        >
          {!contextMenu.item.isFolder && (
            <>
              <button className="dropdown-item" style={{ display: 'block', width: '100%', padding: '0.35rem 1rem', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => { setContextMenu(null); onOpenFile(contextMenu.item.id); }}>Open</button>
              <hr style={{ margin: '0.5rem 0', borderTop: '1px solid rgba(0,0,0,.15)', opacity: 1 }} />
              <button className="dropdown-item" style={{ display: 'block', width: '100%', padding: '0.35rem 1rem', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => { setContextMenu(null); onDownloadFile(contextMenu.item.id); }}>Download</button>
            </>
          )}
          {userRole === "Admin" && process.env.REACT_APP_WEBSITE === 'CLEARNET' && (
            <>
              {!contextMenu.item.isFolder && <hr style={{ margin: '0.5rem 0', borderTop: '1px solid rgba(0,0,0,.15)', opacity: 1 }} />}
              {!contextMenu.item.isFolder && (
                <>
                  <button className="dropdown-item" style={{ display: 'block', width: '100%', padding: '0.35rem 1rem', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => { setContextMenu(null); handleUpdateFile(contextMenu.item.id, contextMenu.item.parentId, contextMenu.item.name, contextMenu.item.description); }}>Update</button>
                  <hr style={{ margin: '0.5rem 0', borderTop: '1px solid rgba(0,0,0,.15)', opacity: 1 }} />
                </>
              )}
              <button className="dropdown-item" style={{ display: 'block', width: '100%', padding: '0.35rem 1rem', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => { setContextMenu(null); handleRenameItem(contextMenu.item.id, contextMenu.item.parentId, contextMenu.item.name, contextMenu.item.isFolder); }}>Rename</button>
              <hr style={{ margin: '0.5rem 0', borderTop: '1px solid rgba(0,0,0,.15)', opacity: 1 }} />
              <button className="dropdown-item" style={{ display: 'block', width: '100%', padding: '0.35rem 1rem', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => { setContextMenu(null); handleRemoveItem(contextMenu.item.id, contextMenu.item.name, contextMenu.item.isFolder); }}>Remove</button>
            </>
          )}
        </div>,
        document.body
      )}
    </React.Fragment>
  )
}

export default ItemsList;
